import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Trophy, ArrowRight } from "@phosphor-icons/react";

const PENDING_KEY = "fairway_pending_league_code";

export function setPendingLeagueCode(code) {
  if (code) localStorage.setItem(PENDING_KEY, code.toUpperCase());
}
export function popPendingLeagueCode() {
  const c = localStorage.getItem(PENDING_KEY);
  if (c) localStorage.removeItem(PENDING_KEY);
  return c;
}

export default function JoinLeague() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | joining | error | success
  const [error, setError] = useState("");
  const [leagueName, setLeagueName] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Stash code and send to register
      setPendingLeagueCode(code);
      return;
    }
    (async () => {
      setStatus("joining");
      try {
        const { data } = await api.post("/leagues/join", { code: code.toUpperCase() });
        setLeagueName(data.name);
        setStatus("success");
        setTimeout(() => nav(`/app/leagues/${data.id}`, { replace: true }), 900);
      } catch (e) {
        setError(formatApiError(e));
        setStatus("error");
      }
    })();
  }, [user, loading, code, nav]);

  return (
    <div className="app-shell min-h-screen p-6 flex flex-col">
      <div className="flex items-center mb-8" data-testid="join-league-logo">
        <img src="/fairway-logo.png" alt="Fairway" className="h-16 w-auto rounded-2xl" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="h-20 w-20 rounded-full bg-[var(--accent-sand)] flex items-center justify-center mb-4">
          <Trophy size={42} weight="fill" className="text-[var(--accent-terracotta)]" />
        </div>

        <p className="label-uppercase mb-1">League invite</p>
        <div className="font-mono-num font-black text-4xl tracking-widest text-[var(--primary)] mb-3" data-testid="invite-code-display">{code?.toUpperCase()}</div>

        {!user && !loading && (
          <>
            <h1 className="font-heading font-black tracking-tighter-2 text-3xl text-[var(--primary)] mb-2">A mate invited you</h1>
            <p className="text-[var(--text-secondary)] mb-6 max-w-xs">Create your Fairway account and you'll drop right into this league.</p>
            <Link to="/register" data-testid="invite-register-cta" className="btn-primary w-full justify-center">
              Create account & join <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/login" data-testid="invite-login-cta" className="mt-3 text-sm text-[var(--primary)] underline">Already have an account? Sign in</Link>
          </>
        )}

        {user && status === "joining" && (
          <p className="text-[var(--text-secondary)]" data-testid="invite-joining-status">Joining league…</p>
        )}

        {user && status === "success" && (
          <p className="text-[var(--status-good)] font-heading font-bold" data-testid="invite-success-status">
            Joined {leagueName}! Redirecting…
          </p>
        )}

        {user && status === "error" && (
          <div className="surface-card-flat p-4 text-[var(--status-bad)]" data-testid="invite-error">
            {error}
            <Link to="/app/leagues" className="btn-primary mt-3 w-full">Back to leagues</Link>
          </div>
        )}
      </div>
    </div>
  );
}
